import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, type ChangeEvent, type JSX } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";


import {
  FieldStack,
  FormGrid,
  FormPanel,
  FormSection,
  FormShell,
} from "../components/forms/index.js";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert.js";
import { Button } from "../components/ui/button.js";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form.js";
import { Input } from "../components/ui/input.js";
import { useSwitchTenantMutation } from "../lib/api/auth.js";
import { useOnboardingUploadMutation } from "../lib/api/onboarding.js";
import { APIError } from "../lib/http.js";
import { enterApp } from "../lib/tenant-url.js";
import { useAuth } from "../providers/auth-provider.js";

const COMPANY_CHOSEN_KEY = "onshelf.companyChosen";
const SELECTED_STORE_KEY = "onshelf.selectedStoreId";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const optionalTrimmed = z.string().trim().max(255).optional().or(z.literal(""));

const onboardingSchema = z.object({
  // Company
  companyName: z.string().min(1, "Company name is required").max(255),
  companyCode: z.string().min(1, "Company code is required").max(255),
  legalName: optionalTrimmed,
  // Contact
  contactEmail: z
    .string()
    .trim()
    .email("Enter a valid email")
    .max(255)
    .optional()
    .or(z.literal("")),
  contactPhone: optionalTrimmed,
  websiteUrl: z.string().trim().url("Enter a valid URL").max(255).optional().or(z.literal("")),
  // Address
  addressLine1: optionalTrimmed,
  addressLine2: optionalTrimmed,
  city: optionalTrimmed,
  stateRegion: optionalTrimmed,
  postalCode: optionalTrimmed,
  countryCode: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, "Use a 2-letter country code")
    .optional()
    .or(z.literal("")),
  defaultTimezone: z.string().trim().max(64).optional().or(z.literal("")),
  defaultCurrency: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, "Use a 3-letter currency code")
    .optional()
    .or(z.literal("")),
});
type OnboardingFormValues = z.infer<typeof onboardingSchema>;

function blankToUndefined(value: string | undefined): string | undefined {
  const v = value?.trim();
  return v && v.length > 0 ? v : undefined;
}

export default function OnboardingPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const logoFileRef = useRef<File | undefined>(undefined);

  const onboardMut = useOnboardingUploadMutation();
  const switchMut = useSwitchTenantMutation();

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      companyName: "",
      companyCode: "",
      legalName: "",
      contactEmail: "",
      contactPhone: "",
      websiteUrl: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      stateRegion: "",
      postalCode: "",
      countryCode: "",
      defaultTimezone: "UTC",
      defaultCurrency: "INR",
    },
    mode: "onSubmit",
  });

  // Default the contact email to the signed-in user's email once known.
  useEffect(() => {
    if (user?.email && !form.getValues("contactEmail")) {
      form.setValue("contactEmail", user.email);
    }
  }, [user?.email, form]);

  // Auto-suggest the company code from the name until the user edits the code.
  const codeDirty = useRef(false);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    form.setValue("companyName", value, { shouldValidate: form.formState.isSubmitted });
    if (!codeDirty.current) {
      form.setValue("companyCode", slugify(value), {
        shouldValidate: form.formState.isSubmitted,
      });
    }
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>): void => {
    logoFileRef.current = e.target.files?.[0] ?? undefined;
  };

  const onSubmit = async (values: OnboardingFormValues): Promise<void> => {
    if (!user) return;
    const result = await onboardMut.mutateAsync({
      email: user.email,
      companyName: values.companyName.trim(),
      // The API requires company_slug; derive it from the code for URL stability.
      companySlug: slugify(values.companyCode) || slugify(values.companyName),
      companyCode: values.companyCode.trim() || undefined,
      legalName: blankToUndefined(values.legalName),
      contactEmail: blankToUndefined(values.contactEmail),
      contactPhone: blankToUndefined(values.contactPhone),
      websiteUrl: blankToUndefined(values.websiteUrl),
      addressLine1: blankToUndefined(values.addressLine1),
      addressLine2: blankToUndefined(values.addressLine2),
      city: blankToUndefined(values.city),
      stateRegion: blankToUndefined(values.stateRegion),
      postalCode: blankToUndefined(values.postalCode),
      countryCode: blankToUndefined(values.countryCode)?.toUpperCase(),
      defaultTimezone: blankToUndefined(values.defaultTimezone),
      defaultCurrency: blankToUndefined(values.defaultCurrency)?.toUpperCase(),
      logoFile: logoFileRef.current,
    });
    const switchRes = await switchMut.mutateAsync({ tenantId: result.tenantId });
    try {
      localStorage.setItem(COMPANY_CHOSEN_KEY, "1");
    } catch {
      /* ignore disabled storage */
    }
    try {
      localStorage.removeItem(SELECTED_STORE_KEY);
    } catch {
      /* ignore disabled storage */
    }
    queryClient.clear();
    await queryClient.refetchQueries({ queryKey: ["auth", "me"] });
    enterApp(switchRes.user.tenantCode);
  };

  const error = onboardMut.error ?? switchMut.error;
  const submitError = error
    ? error instanceof APIError
      ? error.displayDetail
      : (error as Error).message
    : null;

  const isPending = onboardMut.isPending || switchMut.isPending;

  return (
    <main className="relative flex min-h-full items-center justify-center bg-background p-6">
      {/* Escape hatch: a user who landed here on the wrong account (or who
          doesn't want to create a company) needs a way out. */}
      <div className="absolute right-4 top-4 flex items-center gap-3">
        {user?.email ? (
          <span className="hidden text-xs text-muted-foreground sm:inline">{user.email}</span>
        ) : null}
        <Button variant="outline" size="sm" onClick={() => void logout()}>
          Sign out
        </Button>
      </div>
      <FormShell size="md" className="w-full">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-foreground">Create a company</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Set up your company workspace to get started.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4" noValidate>
            <FormPanel>
              <FormSection
                title="Company"
                description="Identity used across navigation and reports."
              >
                <FormGrid columns={2}>
                  <FormField
                    control={form.control}
                    name="companyName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company name</FormLabel>
                        <FormControl>
                          <Input
                            autoFocus
                            placeholder="Acme Retail"
                            {...field}
                            onChange={handleNameChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="companyCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="acme-retail"
                            {...field}
                            onChange={(e) => {
                              codeDirty.current = true;
                              field.onChange(e);
                            }}
                          />
                        </FormControl>
                        <FormDescription>Lowercase letters, numbers, hyphens.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormGrid>
                <FormField
                  control={form.control}
                  name="legalName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Legal name (optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Retail Pvt. Ltd." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormSection>
            </FormPanel>

            <FormPanel>
              <FormSection
                title="Contact"
                description="How customers and OnShelf reach this company."
              >
                <FormField
                  control={form.control}
                  name="contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact email</FormLabel>
                      <FormControl>
                        <Input type="email" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormGrid columns={2}>
                  <FormField
                    control={form.control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact phone (optional)</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="+91 98765 43210" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="websiteUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website (optional)</FormLabel>
                        <FormControl>
                          <Input type="url" placeholder="https://acme.example" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormGrid>
              </FormSection>
            </FormPanel>

            <FormPanel>
              <FormSection
                title="Address"
                description="Primary business address and regional defaults."
              >
                <FormField
                  control={form.control}
                  name="addressLine1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address line 1 (optional)</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="addressLine2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address line 2 (optional)</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormGrid columns={2}>
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City (optional)</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="stateRegion"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State / region (optional)</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormGrid>
                <FormGrid columns={3}>
                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postal code (optional)</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="countryCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country code (optional)</FormLabel>
                        <FormControl>
                          <Input placeholder="IN" maxLength={2} {...field} />
                        </FormControl>
                        <FormDescription>2-letter ISO code.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="defaultCurrency"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Currency</FormLabel>
                        <FormControl>
                          <Input placeholder="INR" maxLength={3} {...field} />
                        </FormControl>
                        <FormDescription>3-letter ISO code.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </FormGrid>
                <FormField
                  control={form.control}
                  name="defaultTimezone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Default timezone</FormLabel>
                      <FormControl>
                        <Input placeholder="UTC" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormSection>
            </FormPanel>

            <FormPanel>
              <FormSection title="Branding" description="Optional company logo.">
                <FieldStack>
                  <FormLabel>Logo (optional)</FormLabel>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleLogoChange}
                  />
                  <FormDescription>PNG, JPEG, or WEBP, up to 5 MB.</FormDescription>
                </FieldStack>
              </FormSection>
            </FormPanel>

            {submitError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not create company</AlertTitle>
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="flex flex-col gap-3">
              <Button type="submit" className="w-full" disabled={isPending}>
                {isPending ? "Creating…" : "Create company"}
              </Button>
              <div className="text-center text-sm">
                <Link
                  to="/select-company"
                  className="text-muted-foreground hover:text-foreground"
                >
                  Back to companies
                </Link>
              </div>
            </div>
          </form>
        </Form>
      </FormShell>
    </main>
  );
}
